import { t, tr } from "../i18n";
import { castName, PORTRAIT, pimg, sprite } from "../art/cast";
import { PIP } from "../art/pip-scenes";
import { $, h, restartAnimation } from "../lib/dom";
import { resolve, sleep } from "../lib/util";
import { closeDialog } from "./dialog";
import { post } from "./log";
import { clearMovement } from "./player";
import { dept, rt, S, sessionGuard } from "./state";
import { clock, renderHUD } from "./time";
import type { EndingStep, PipCfg } from "./types";
import { renderWaits } from "./waits";
import { closeZoom } from "./zoom";

/* ---------- event banner ---------- */

let bannerTimer: ReturnType<typeof setTimeout> | undefined;

/** Big centred title card for a plot event; `red` adds a screen shake. Pauses the idle clock briefly. */
export function eventBanner(title: string, sub?: string, red = false): void {
  if (!S.live) return;
  const b = $("#evBanner");
  b.classList.toggle("red", red);
  $("#evBanner .t").textContent = title;
  $("#evBanner .s").textContent = (sub ? sub + t("log.sep") : "") + clock();
  restartAnimation(b, "show");
  if (red) restartAnimation($("#stage"), "shake");
  S.pauseUntil = performance.now() + 2300;
  clearTimeout(bannerTimer);
  bannerTimer = setTimeout(() => b.classList.remove("show"), 2100);
  post("sys", "narr", null, tr(`【${title}】${sub ?? ""}`, `[${title}] ${sub ?? ""}`), { silent: true });
}

/* ---------- ending cutscene ---------- */

function cineLine(who: string | null, text: string): void {
  const b = $("#cineBox");
  b.replaceChildren();
  if (who && PORTRAIT[who]) b.appendChild(pimg(who));
  b.appendChild(h("div", {}, h("b", { text: castName(who) }), h("div", { text })));
  b.classList.add("show");
  post("sys", who ? "npc" : "narr", who, text, { silent: true, noflag: true });
}

/** Letterboxed cutscene, then the ending card. */
export async function playEnding(
  code: string,
  name: string,
  steps: EndingStep[],
  summary: string | (() => string),
): Promise<void> {
  if (!S.live || S.ending || S.ended) return;
  const alive = sessionGuard();
  S.ending = { code, name, summary };
  S.cine = true;
  S.canEnd = false;
  closeZoom();
  if (S.dialog) closeDialog();
  clearMovement();
  $("#call").classList.remove("show");
  S.waits = [];
  S.flags = {};
  renderWaits();
  renderHUD();
  $("#stage").classList.add("cine");
  await sleep(700 * rt.cineScale);
  for (const st of steps) {
    if (!alive()) return;
    st.fn?.();
    if (st.text) cineLine(st.who ?? null, st.text);
    await sleep((st.wait ?? (st.text ? 2600 : 900)) * rt.cineScale);
  }
  if (!alive()) return;
  $("#cineBox").classList.remove("show");
  $("#endCard .code").textContent = t("end.code", { code });
  $("#endCard .nm").textContent = name;
  $("#endCard p").textContent = resolve(summary);
  $("#endCard").classList.add("show");
}

/* ---------- picture-in-picture ---------- */

const pipCv = $<HTMLCanvasElement>("#pipc");
const pc = pipCv.getContext("2d");
if (pc) pc.imageSmoothingEnabled = false;

/** Show (or hide with null) the corner vignette following an off-screen character. */
export function pipSet(cfg: PipCfg | null): void {
  if (!S.live) return;
  S.pip = cfg;
  $("#pip").classList.toggle("show", !!cfg);
  S.pipText = null;
}

export function drawPip(now: number): void {
  if (!S.live || !S.pip || !pc) return;
  const sc = S.pip.scene();
  pc.clearRect(0, 0, 96, 54);
  (PIP[sc.key] ?? PIP.run)?.(pc, now, sprite(S.pip.sprite ?? dept.attending), sc.urg);
  const eta = S.pip.eta?.() ?? "";
  const txt = `${sc.text}|${eta}`;
  if (txt !== S.pipText) {
    S.pipText = txt;
    const pt = $("#pip .pt");
    pt.replaceChildren(h("b", { text: S.pip.title ?? castName(dept.attending) }), t("log.sep") + sc.text);
    if (S.pip.eta) pt.appendChild(h("span", { className: "eta", text: eta }));
  }
}
